import React from "react";
import type { RenderImageNode as RenderImageNodeModel } from "../../../util/src/layout-compiler/types.js";
import { getRenderNodeClipStyle } from "./renderClipStyle.js";
import { useRuntimeNodeProps } from "./useRuntimeElementRef.js";
import { useProtectedMediaSrc } from "./useProtectedMediaSrc";
import { RenderNodeTooltipShell } from "./RenderNodeTooltipShell.js";

type RenderImageNodeProps = {
  node: RenderImageNodeModel;
  scale: number;
};

function composeTransform(
  rotation: number,
  styleTransform?: unknown,
): string | undefined {
  const rotationTransform =
    rotation !== 0 ? `rotate(${rotation}deg)` : undefined;
  const extra =
    typeof styleTransform === "string" && styleTransform.trim().length > 0
      ? styleTransform
      : undefined;

  if (rotationTransform && extra) return `${rotationTransform} ${extra}`;
  return rotationTransform ?? extra;
}

export function RenderImageNode({ node, scale }: RenderImageNodeProps) {
  const runtimeProps = useRuntimeNodeProps(node);
  const resolvedSrc = useProtectedMediaSrc(node.src);
  const clipStyle = getRenderNodeClipStyle(node, scale);

  if (!resolvedSrc) {
    return null;
  }

  return (
    <RenderNodeTooltipShell
      tooltip={node.tooltip}
      ownerNodeKey={node.key ?? node.id}
    >
      <img
        {...runtimeProps}
        src={resolvedSrc}
        alt={node.alt ?? ""}
        draggable={false}
        style={{
          ...(node.style.position === "relative"
            ? {
                position: "relative",
                flexShrink: 0,
              }
            : {
                position: "absolute",
                left: node.x * scale,
                top: node.y * scale,
              }),
          width: node.width * scale,
          height: node.height * scale,
          zIndex: node.zIndex,
          objectFit:
            (node.objectFit as React.CSSProperties["objectFit"]) ?? "contain",
          boxSizing: "border-box",
          userSelect: "none",
          pointerEvents: node.clickable || node.tooltip ? "auto" : "none",
          ...(node.style as React.CSSProperties),
          transform: composeTransform(node.rotation, node.style?.transform),
          transformOrigin: "center center",
          ...clipStyle,
        }}
      />
    </RenderNodeTooltipShell>
  );
}
